import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FileSignature, Search, Printer, Eye, Trash2 } from "lucide-react";
import api from "../api";
import { toast } from "sonner";
import DocumentPreviewDialog from "../components/print/DocumentPreviewDialog";

const STATUS_STYLES = {
  Draft: "bg-slate-100 text-slate-600",
  Sent: "bg-emerald-100 text-emerald-700",
};

export default function Letters() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/letters", { params: { q } }).then((r) => setRows(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q]);

  const removeLetter = async (letter) => {
    if (!window.confirm(`Delete letter ${letter.ref_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/letters/${letter.id}`);
      toast.success(`Letter ${letter.ref_no} deleted`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Letter delete nahi ho paaya");
    }
  };

  return (
    <div className="sgp-page" data-testid="letters-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="sgp-page-title">Letter Register</h1>
          <p className="sgp-page-subtitle">Supply letters, EMD refund requests, authority letters, tender cover letters — all on company letterhead.</p>
        </div>
        <Link to="/letters/new" data-testid="new-letter-btn"
          className="sgp-primary-btn">
          <Plus className="h-4 w-4" /> New Letter
        </Link>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, addressee, ref no..."
          className="sgp-input pl-9" data-testid="letters-search" />
      </div>

      <div className="sgp-table-shell overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr>
            <th className="sgp-th">Ref. No.</th><th className="sgp-th">Date</th><th className="sgp-th">Type</th>
            <th className="sgp-th">To</th><th className="sgp-th">Subject</th><th className="sgp-th">Status</th><th className="sgp-th"></th>
          </tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id}>
                <td className="sgp-td font-mono text-xs">{l.ref_no}</td>
                <td className="sgp-td">{l.date}</td>
                <td className="sgp-td">{l.letter_type}</td>
                <td className="sgp-td">{l.addressee_name || l.to_org || "-"}</td>
                <td className="sgp-td max-w-xs truncate">{l.subject}</td>
                <td className="sgp-td">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[l.status] || "bg-slate-100 text-slate-600"}`}>{l.status}</span>
                </td>
                <td className="sgp-td">
                  <div className="flex items-center gap-2">
                    <button data-testid={`preview-letter-${l.id}`} type="button" onClick={() => setPreview({ kind: "letter", id: l.id, label: `Letter ${l.ref_no}` })}
                      className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 text-sm font-semibold" title="Preview">
                      <Eye className="h-4 w-4" /> Preview
                    </button>
                    <Link data-testid={`print-letter-${l.id}`} to={`/print/letter/${l.id}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-900 text-sm font-semibold" title="Print / PDF">
                      <Printer className="h-4 w-4" /> Print
                    </Link>
                    <button data-testid={`delete-letter-${l.id}`} type="button" onClick={() => removeLetter(l)}
                      className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-800 text-sm font-semibold" title="Delete">
                      <Trash2 className="h-4 w-4" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="sgp-td text-center text-slate-400 py-10">
                <FileSignature className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                No letters yet. Write your first one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <DocumentPreviewDialog open={!!preview} onClose={() => setPreview(null)} kind={preview?.kind} id={preview?.id} label={preview?.label} />
    </div>
  );
}
